/**
 * Process playback state — the step scrubber, auto-play, and per-process
 * walker state (branch selections + expanded subprocesses) that drive the
 * process view.
 *
 * The heavy walker derivation (active process, swimlane bridge, walk
 * result) already lives in `useProcessOrchestrator`; this hook owns the
 * *interactive* state around it and re-exposes the orchestration so the
 * board/sidebar read one object.
 *
 * `activeProcessId` is NOT owned here — it's part of the selection
 * (`useSelection`), passed in. Switching process resets the scrubber.
 * `branchSelection`/`expandedSubprocesses` are kept per-process so flipping
 * between two processes preserves each one's drill state.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ArchitectureModel, StableId, Subprocess } from "@archlang/scene/arch-model";
import type { ResolvedModel, FailureAnswer, DelayAnswer } from "@archlang/engine";
import {
  useProcessOrchestrator,
  type ProcessOrchestration,
} from "../process/useProcessOrchestrator.js";
import { findEnclosingRange } from "../process/stepRanges.js";
import { buildPathGates, gateOnAdvance, type PathGate } from "../process/pathGates.js";

/** Per-process walker state. Mutable maps — the orchestrator accepts these
 *  (it reads them as readonly) and the process panels expect mutable ones. */
export interface WalkerState {
  branchSelection: Map<string, string>;
  expandedSubprocesses: Set<string>;
  /**
   * P10 interactive-capture overlay — the extended pin answers the scrubber
   * accumulates beyond if/try (`branchSelection`): which `select` cases ran,
   * injected step failures, and injected step delays. These populate the engine
   * {@link import("@archlang/engine").CaptureSession} for "Save as test" (see
   * `captureTest.ts`); they are NOT consumed by `walkProcess`, which renders the
   * structure off `branchSelection` — they surface live as step overlays/badges.
   * Optional so the many bare `{ branchSelection, expandedSubprocesses }` literals
   * (and the engine walker state they double as) stay valid; `newWalkerState`
   * initialises them for the live capture path.
   */
  selectRuns?: Map<string, number[]>;
  failures?: Map<string, FailureAnswer>;
  delays?: Map<string, DelayAnswer>;
}

/** A fresh, empty per-process walker state — one factory so every construction
 *  site carries the full field set (the overlay maps included). */
export function newWalkerState(): WalkerState {
  return {
    branchSelection: new Map<string, string>(),
    expandedSubprocesses: new Set<string>(),
    selectRuns: new Map<string, number[]>(),
    failures: new Map<string, FailureAnswer>(),
    delays: new Map<string, DelayAnswer>(),
  };
}

export interface ProcessPlaybackController extends ProcessOrchestration {
  /** Walker state for the active process (or a stable empty fallback). */
  readonly activeWalkerState: WalkerState;
  readonly activeStepIndex: number | null;
  readonly setActiveStepIndex: React.Dispatch<React.SetStateAction<number | null>>;
  readonly autoPlay: boolean;
  readonly setAutoPlay: React.Dispatch<React.SetStateAction<boolean>>;
  /** Advance to the next step (skipping collapsed/parallel groups), stopping
   *  auto-play at the end. The view drives the cadence (after the droplet). */
  readonly advanceStep: () => void;
  /**
   * A pending path decision: the advance stopped at an exclusive frame
   * (if/try) with no explicit branch pick. The scrubber renders the choices;
   * `choosePath` records one and resumes, `dismissChoice` stays put.
   */
  readonly pendingChoice: PathGate | null;
  readonly choosePath: (label: string) => void;
  readonly dismissChoice: () => void;
  /**
   * Gate hook for view-driven forward moves (the scrubber's ▶): returns true
   * (and surfaces the chooser) when advancing to `toIndex` requires a path
   * decision — the caller must then NOT move.
   */
  readonly gateAdvance: (toIndex: number) => boolean;
  /** Pick (or, with empty label, clear) a branch and clamp the scrubber. */
  readonly selectBranch: (nodeId: string, label: string) => void;
  /** Replace the active process's branch selections wholesale — a fresh walker
   *  state, clearing any prior picks. The "load this test's answered trace"
   *  reset (P7 panel), so a replay never inherits a previous test's arms. */
  readonly replaceBranchSelection: (selections: ReadonlyMap<string, string>) => void;
  /**
   * P10 interactive capture — inject the extended pin answers beyond if/try. Set
   * (or clear, with `null`) which `select` cases ran at a select container, an
   * injected failure at a step, or an injected delay at a step. Each accumulates
   * into the {@link import("@archlang/engine").CaptureSession} that "Save as test"
   * lifts; live they overlay the affected step as a badge (`walkProcess` renders
   * the structure off `branchSelection` and is unaffected).
   */
  readonly setSelectRuns: (nodeId: string, indices: readonly number[] | null) => void;
  readonly setFailure: (nodeId: string, answer: FailureAnswer | null) => void;
  readonly setDelay: (nodeId: string, answer: DelayAnswer | null) => void;
  /** Expand/collapse a subprocess in the active process's walker state. */
  readonly toggleSubprocess: (nodeId: string, expanded: boolean) => void;
  /** Subprocess name → description, for the README under an expanded `do`. */
  readonly subprocessDescriptions: ReadonlyMap<string, string>;
}

/**
 * Optional controlled scrubber state. When supplied (by
 * `ProcessPlaybackProvider`, backed by the per-tab `WorkspaceStore.process`
 * slice), the step index + auto-play live OUTSIDE the hook so they survive a
 * tab switch — the store restores each tab's scrubber position. When omitted
 * (tests, standalone), the hook owns them internally as before.
 */
export interface ProcessScrubberControl {
  readonly stepIndex: number | null;
  readonly setStepIndex: React.Dispatch<React.SetStateAction<number | null>>;
  readonly autoPlay: boolean;
  readonly setAutoPlay: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useProcessPlayback(
  model: ArchitectureModel | null,
  activeProcessId: StableId | null,
  controlled?: ProcessScrubberControl,
  // The resolved engine model for the current compile — the orchestrator
  // resolves the process IR + contexts off it (the synthesized flow view).
  resolvedModel: ResolvedModel | null = null,
): ProcessPlaybackController {
  const [walkerStateByProcess, setWalkerStateByProcess] = useState<Map<StableId, WalkerState>>(
    () => new Map(),
  );

  // Stable empty fallback so we don't reallocate every render.
  const emptyWalkerState = useMemo<WalkerState>(() => newWalkerState(), []);
  const [internalStepIndex, setInternalStepIndex] = useState<number | null>(null);
  const [internalAutoPlay, setInternalAutoPlay] = useState(false);
  // Controlled (per-tab store) wins; else the hook's own state.
  const activeStepIndex = controlled ? controlled.stepIndex : internalStepIndex;
  const setActiveStepIndex = controlled ? controlled.setStepIndex : setInternalStepIndex;
  const autoPlay = controlled ? controlled.autoPlay : internalAutoPlay;
  const setAutoPlay = controlled ? controlled.setAutoPlay : setInternalAutoPlay;

  // Reset the scrubber whenever the process changes — but ONLY when
  // uncontrolled. When controlled, the store resets per-tab on a within-tab
  // process change and PRESERVES it across tab switches, so resetting here
  // would wipe the restored position every time the active tab flips.
  useEffect(() => {
    if (controlled) return;
    setInternalStepIndex(null);
    setInternalAutoPlay(false);
  }, [activeProcessId, controlled]);

  const activeWalkerState =
    (activeProcessId && walkerStateByProcess.get(activeProcessId)) || emptyWalkerState;

  const orchestration = useProcessOrchestrator(
    model,
    activeProcessId,
    activeWalkerState,
    activeStepIndex,
    resolvedModel,
  );
  const { walkResult } = orchestration;

  // Keep walkResult + the scrubber index in refs so the advance/gate closures
  // read the live projection without re-creating the callbacks every render.
  const walkResultRef = useRef(walkResult);
  walkResultRef.current = walkResult;
  const activeStepIndexRef = useRef(activeStepIndex);
  activeStepIndexRef.current = activeStepIndex;
  const branchSelectionRef = useRef(activeWalkerState.branchSelection);
  branchSelectionRef.current = activeWalkerState.branchSelection;

  // Path gates: stepping INTO an exclusive frame (if/try) without an explicit
  // branch pick pauses and asks instead of silently walking the default arm.
  const pathGates = useMemo(
    () => (walkResult ? buildPathGates(walkResult) : null),
    [walkResult],
  );
  const pathGatesRef = useRef(pathGates);
  pathGatesRef.current = pathGates;
  const [pendingChoice, setPendingChoice] = useState<PathGate | null>(null);

  const gateAdvance = useCallback(
    (toIndex: number): boolean => {
      const gates = pathGatesRef.current;
      if (!gates) return false;
      const gate = gateOnAdvance(gates, branchSelectionRef.current, activeStepIndexRef.current, toIndex);
      if (!gate) return false;
      setPendingChoice(gate);
      setAutoPlay(false);
      return true;
    },
    [setAutoPlay],
  );

  // Advance to the next step (skipping whole collapsed/parallel groups), or
  // stop auto-play at the end. Steps carry stable GLOBAL stepIndices (gaps
  // where unselected alternative branches would have lived), so navigate by
  // POSITION in walkResult.steps to always land on a real selected step.
  //
  // The CADENCE is driven by the view (SwimlaneView advances once the travel
  // droplet has reached the node + a dwell), not a fixed timer — so the
  // animation always plays fully regardless of edge length.
  const advanceStep = useCallback(() => {
    const walk = walkResultRef.current;
    if (!walk || walk.steps.length === 0) return;
    // Auto-play / advance follows the forward happy path; saga compensation
    // (back-flow) steps are excluded — they're the failure path.
    const steps = walk.steps.filter((s) => !s.modifiers.compensation);
    if (steps.length === 0) return;
    const prev = activeStepIndexRef.current;
    const cur = prev ?? steps[0]!.stepIndex;
    const range = findEnclosingRange(walk, cur);
    let nextStable: number | null = null;
    if (range) {
      const found = steps.find((s) => s.stepIndex > range.endIndex);
      nextStable = found ? found.stepIndex : null;
    } else {
      const pos = steps.findIndex((s) => s.stepIndex === cur);
      if (pos >= 0 && pos + 1 < steps.length) nextStable = steps[pos + 1]!.stepIndex;
    }
    if (nextStable === null) {
      setAutoPlay(false);
      setActiveStepIndex(steps[steps.length - 1]!.stepIndex);
      return;
    }
    if (gateAdvance(nextStable)) return; // paused at a path decision
    setActiveStepIndex(nextStable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateAdvance]);

  // Resume the advance the gate interrupted, once the pick has re-walked the
  // projection (the gate now passes — the selection is explicit).
  const resumeAfterChoiceRef = useRef(false);
  const advanceStepRef = useRef(advanceStep);
  advanceStepRef.current = advanceStep;
  useEffect(() => {
    if (!resumeAfterChoiceRef.current) return;
    resumeAfterChoiceRef.current = false;
    advanceStepRef.current();
  }, [walkResult]);

  const dismissChoice = useCallback(() => setPendingChoice(null), []);

  // Any process switch invalidates a pending decision.
  useEffect(() => setPendingChoice(null), [activeProcessId]);

  const selectBranch = useCallback(
    (nodeId: string, label: string) => {
      if (!activeProcessId) return;
      setWalkerStateByProcess((prev) => {
        const next = new Map(prev);
        const cur = next.get(activeProcessId) ?? newWalkerState();
        const branchSelection = new Map(cur.branchSelection);
        // Empty label = unselect → walker falls back to its default branch.
        if (label === "") branchSelection.delete(nodeId);
        else branchSelection.set(nodeId, label);
        next.set(activeProcessId, { ...cur, branchSelection });
        return next;
      });
    },
    [activeProcessId],
  );

  const replaceBranchSelection = useCallback(
    (selections: ReadonlyMap<string, string>) => {
      if (!activeProcessId) return;
      setWalkerStateByProcess((prev) => {
        const next = new Map(prev);
        const cur = next.get(activeProcessId) ?? newWalkerState();
        // A fresh replay must NOT inherit the prior session's answers — clear the
        // P10 overlay (select-runs / failures / delays) alongside branchSelection,
        // else a stale inject leaks into the live badges and the next capture.
        // Subprocess expansion (a view preference, not a pinned answer) is kept.
        next.set(activeProcessId, {
          ...newWalkerState(),
          expandedSubprocesses: cur.expandedSubprocesses,
          branchSelection: new Map(selections),
        });
        return next;
      });
    },
    [activeProcessId],
  );

  // One writer for the active process's walker state — the overlay mutators
  // (select-runs / failure / delay) share it so each stays a two-liner.
  const mutateActive = useCallback(
    (mutate: (cur: WalkerState) => WalkerState) => {
      if (!activeProcessId) return;
      setWalkerStateByProcess((prev) => {
        const next = new Map(prev);
        next.set(activeProcessId, mutate(next.get(activeProcessId) ?? newWalkerState()));
        return next;
      });
    },
    [activeProcessId],
  );

  const setSelectRuns = useCallback(
    (nodeId: string, indices: readonly number[] | null) =>
      mutateActive((cur) => {
        const selectRuns = new Map(cur.selectRuns ?? []);
        if (indices === null) selectRuns.delete(nodeId);
        else selectRuns.set(nodeId, [...indices]);
        return { ...cur, selectRuns };
      }),
    [mutateActive],
  );

  const setFailure = useCallback(
    (nodeId: string, answer: FailureAnswer | null) =>
      mutateActive((cur) => {
        const failures = new Map(cur.failures ?? []);
        if (answer === null) failures.delete(nodeId);
        else failures.set(nodeId, answer);
        return { ...cur, failures };
      }),
    [mutateActive],
  );

  const setDelay = useCallback(
    (nodeId: string, answer: DelayAnswer | null) =>
      mutateActive((cur) => {
        const delays = new Map(cur.delays ?? []);
        if (answer === null) delays.delete(nodeId);
        else delays.set(nodeId, answer);
        return { ...cur, delays };
      }),
    [mutateActive],
  );

  // Record the gate's pick and resume the interrupted advance on the re-walk.
  const choosePath = useCallback(
    (label: string) => {
      if (!pendingChoice) return;
      selectBranch(pendingChoice.frameId, label);
      setPendingChoice(null);
      resumeAfterChoiceRef.current = true;
    },
    [pendingChoice, selectBranch],
  );

  // Reconcile the scrubber against the CURRENT projection. Indices are
  // canonical (all-branches, gaps where alternatives live), so a branch flip
  // or model edit can leave the held index pointing at a step that is no
  // longer on the selected walk. Snap to the nearest PRECEDING selected step
  // (the user's position, minus the abandoned branch), else the first step.
  // Runs on the post-change walk — a pre-change clamp can only guess.
  useEffect(() => {
    if (activeStepIndex === null || !walkResult) return;
    if (walkResult.byIndex.has(activeStepIndex)) return;
    let preceding: number | null = null;
    for (const s of walkResult.steps) {
      if (s.stepIndex > activeStepIndex) break; // steps ascend in document order
      preceding = s.stepIndex;
    }
    setActiveStepIndex(preceding ?? walkResult.steps[0]?.stepIndex ?? null);
  }, [walkResult, activeStepIndex, setActiveStepIndex]);

  const toggleSubprocess = useCallback(
    (nodeId: string, expanded: boolean) => {
      if (!activeProcessId) return;
      setWalkerStateByProcess((prev) => {
        const next = new Map(prev);
        const cur = next.get(activeProcessId) ?? newWalkerState();
        const expandedSubprocesses = new Set(cur.expandedSubprocesses);
        if (expanded) expandedSubprocesses.add(nodeId);
        else expandedSubprocesses.delete(nodeId);
        next.set(activeProcessId, { ...cur, expandedSubprocesses });
        return next;
      });
    },
    [activeProcessId],
  );

  // README source for an expanded `do` block. Derived from the ACTIVE
  // process's scope-aware subprocess map — NOT a global sweep — because the
  // walk keys `do` nodes by their as-written target and the engine resolves
  // splice targets per process (graph.ts subprocessScopeFor): a global
  // bare-keyed map let one process's local `helper` README overwrite
  // another's, and a qualified `do Billing.BookSlot` never found its README
  // at all (r54). The FQN alias mirrors the engine's — a module-scoped
  // subprocess is addressable by both its bare name and `<owner>.<name>`.
  const subprocessDescriptions = useMemo(() => {
    const map = new Map<string, string>();
    const add = (key: string, sp: { readonly description?: string }): void => {
      if (sp.description && !map.has(key)) map.set(key, sp.description);
    };
    // Nested subprocesses (declared inside a subprocess body) are reachable
    // from the parent's inlined body by bare name; scope-resolved entries win
    // over any nested name collision (first write wins throughout).
    const collectNested = (subs: readonly Subprocess[] | undefined): void => {
      if (!subs) return;
      for (const sp of subs) {
        add(sp.name, sp);
        collectNested(sp.subprocesses);
      }
    };
    for (const [name, sp] of orchestration.subprocessByName) {
      add(name, sp);
      if (sp.ownerPath && sp.ownerPath.length > 0) {
        add([...sp.ownerPath, sp.name].join("."), sp);
      }
    }
    for (const sp of orchestration.subprocessByName.values()) {
      collectNested(sp.subprocesses);
    }
    // Named PROCESSES are splice targets too (v0.9.0 §2.5) and equal citizens
    // of the same `do` contract — mirror walkerContextFor's registration:
    // bare name scope-limited (free-standing, or owner an ancestor of the
    // active process's owner), FQN globally addressable. Subprocess-family
    // keys were added first, so a same-name subprocess wins — matching the
    // walker's spread order (r55). A spliced process's own local subprocesses
    // are reachable inside its inlined body, so collect them too.
    const activeOwner = orchestration.activeProcess?.ownerPath ?? [];
    const isPrefix = (a: readonly string[], b: readonly string[]) =>
      a.length <= b.length && a.every((seg, i) => seg === b[i]);
    for (const p of model?.processes ?? []) {
      // Anonymous processes serialize with an empty name — never splice targets.
      if (!p.name) continue;
      const owned = p.ownerPath && p.ownerPath.length > 0;
      const inScope = !owned || isPrefix(p.ownerPath!, activeOwner);
      if (inScope) add(p.name, p);
      if (owned) add([...p.ownerPath!, p.name].join("."), p);
      if (inScope || owned) collectNested(p.subprocesses);
    }
    return map;
  }, [orchestration.subprocessByName, orchestration.activeProcess, model]);

  return {
    ...orchestration,
    activeWalkerState,
    activeStepIndex,
    setActiveStepIndex,
    autoPlay,
    setAutoPlay,
    advanceStep,
    pendingChoice,
    choosePath,
    dismissChoice,
    gateAdvance,
    selectBranch,
    replaceBranchSelection,
    setSelectRuns,
    setFailure,
    setDelay,
    toggleSubprocess,
    subprocessDescriptions,
  };
}
